import { lazy, Suspense, useState } from 'react'
import { useViewer } from './useViewer'
import { useInteraction } from './useInteraction'
import { useAppStore } from '@core/store'
import { useSectionBox } from '@viewer/section-box/useSectionBox'
import { usePreenchimentoDeCorte } from '@viewer/section-box/usePreenchimentoDeCorte'
import { useSelecaoAtravessa } from '@viewer/selection/useSelecaoAtravessa'
import SectionBoxPanel from '@viewer/section-box/SectionBoxPanel'
import SectionBoxOverlay from '@viewer/section-box/SectionBoxOverlay'
import PlanosDeCorteOverlay from '@viewer/section-box/PlanosDeCorteOverlay'
import ViewCube from '@viewer/view-cube'
import HoverOverlay from '@viewer/selection/HoverOverlay'
import MarqueeOverlay from '@viewer/selection/MarqueeOverlay'
import IfcDiffPanel from '@viewer/diff/IfcDiffPanel'
import IdsPanel from '@modules/ids/IdsPanel'
import ClashPanel from '@viewer/clash/ClashPanel'
import { useMeasure } from '@viewer/measure/useMeasure'
import { useWorkPlane } from '@viewer/measure/useWorkPlane'
import { useCoordsMarkers } from '@viewer/measure/useCoordsMarkers'
import CoordinatesHUD from '@viewer/measure/CoordinatesHUD'
import OrigemEmFocoAviso from './OrigemEmFocoAviso'
import { useAnnotations } from '@viewer/annotations/useAnnotations'
import Views2DSurface from '@viewer/views2d/Views2DSurface'
import SurfaceSwitch from '@viewer/views2d/SurfaceSwitch'
import ContextMenu from './ContextMenu'
import EmptyState from './EmptyState'
import DropZone from './DropZone'
import BcfPinsOverlay from '@viewer/bcf/BcfPinsOverlay'
import BcfPinsPanel from '@viewer/bcf/BcfPinsPanel'
import BcfPinsBotao from '@viewer/bcf/BcfPinsBotao'
import PdfReviewHost from '@modules/pdf-review/PdfReviewHost'
import VersionHistoryPanel from '@viewer/version-history/VersionHistoryPanel'
import PointCloudPanel from '@viewer/point-cloud/PointCloudPanel'
import ModelIntelligencePanel from '@viewer/intelligence/ModelIntelligencePanel'

// O painel de mapa arrasta o leaflet (~440 kB) e o proj4 (~240 kB, mais
// o mgrs que ele traz atrás). Quem nunca abre o mapa não deve pagar
// nada disso no arranque, por isso o chunk só é pedido à primeira
// abertura do painel.
const MapPanel = lazy(() => import('@viewer/map/MapPanel'))

function Viewer3D() {
  const { containerRef, error } = useViewer()
  const marqueeMode = useAppStore((s) => s.marqueeMode)
  const measureMode = useAppStore((s) => s.measureMode)
  const hover = useAppStore((s) => s.hover)
  useSectionBox()
  usePreenchimentoDeCorte()
  useSelecaoAtravessa()
  useMeasure()
  useWorkPlane()
  useCoordsMarkers()
  useAnnotations()
  const annotationPlaceMode = useAppStore((s) => s.annotationPlaceMode)
  const measureDeleteMode = useAppStore((s) => s.measureDeleteMode)
  const { marqueeOverlay } = useInteraction(containerRef)
  // Superfície da área central: 3D (o canvas) ou 2D (o desenho). O
  // canvas do 3D NUNCA se desmonta: a superfície 2D é uma camada opaca
  // por cima (z-30), por isso o contexto WebGL, os modelos carregados e
  // a câmara ficam exactamente como estavam.
  const surface = useAppStore((s) => s.viewerSurface)

  // Montagem "pegajosa" do painel de mapa: só monta à primeira abertura
  // e daí em diante fica montado, tal como antes. É o próprio MapPanel
  // que lê mapPanelOpen e devolve null quando fechado, e o efeito de
  // fecho dele é que destrói a instância do Leaflet (map.remove());
  // desmontá-lo ao fechar saltaria essa limpeza.
  const mapPanelOpen = useAppStore((s) => s.mapPanelOpen)
  const [mapPanelMounted, setMapPanelMounted] = useState(false)
  // Ajuste de estado durante o render (padrão documentado do React): o
  // efeito equivalente obrigaria a um segundo render antes de o pedido
  // do chunk arrancar.
  if (mapPanelOpen && !mapPanelMounted) setMapPanelMounted(true)

  const cursorClass =
    annotationPlaceMode || measureMode || measureDeleteMode || marqueeMode
      ? 'cursor-crosshair'
      : hover
        ? 'cursor-pointer'
        : ''

  return (
    // `isolate`: as camadas de dentro da vista 3D (HUD, revisão de PDF)
    // não sobem acima das janelas da página. Ver core/layout/camadas.ts.
    <div className={`relative isolate h-full w-full ${cursorClass}`}>
      <div ref={containerRef} className="h-full w-full" />

      <SectionBoxOverlay />
      <PlanosDeCorteOverlay />
      <SectionBoxPanel />
      <ViewCube />
      <MarqueeOverlay info={marqueeOverlay} />
      <HoverOverlay />
      <IfcDiffPanel />
      <IdsPanel />
      <ClashPanel />
      <CoordinatesHUD />
      <OrigemEmFocoAviso />
      <ContextMenu />
      <EmptyState />
      <DropZone />
      <BcfPinsOverlay />
      <BcfPinsPanel />
      <PdfReviewHost />
      <VersionHistoryPanel />
      <PointCloudPanel />
      <ModelIntelligencePanel />
      {mapPanelMounted && (
        <Suspense
          fallback={
            <div className="pointer-events-none absolute bottom-3 right-3 z-20 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]/95 px-3 py-2 text-xs text-[var(--color-text-muted)] shadow-2xl backdrop-blur">
              A carregar mapa…
            </div>
          }
        >
          <MapPanel />
        </Suspense>
      )}

      <Views2DSurface />
      {surface === '3d' && (
        <div className="absolute left-2 top-2 z-20 flex items-center gap-2">
          <SurfaceSwitch />
          <BcfPinsBotao />
        </div>
      )}

      {error && (
        <div className="pointer-events-auto absolute inset-x-3 bottom-3 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-sm text-[var(--color-error-text)]">
          {error}
        </div>
      )}
    </div>
  )
}

export default Viewer3D
